import { ChevronLeft, ChevronRight, Users, LogOut, Users2, Lock, ShieldAlert, Ban, ArrowLeft, MonitorPlay, KeyRound, RefreshCw, Film, Play, Pause, Square, Repeat } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { DeviceBadge } from '@/components/DeviceBadge';
import { api, type RoomAccess } from '@/lib/api';
import { prettyModelName } from '@/lib/utils';
import type { ModelFile } from '@/lib/types';
import { MobileArCard } from './MobileArCard';
import { HeadsetCard } from './HeadsetCard';
import { ModelPreview } from './ModelPreview';
import { RoomSettingsDialog } from './RoomSettingsDialog';
import { EndSessionDialog } from './EndSessionDialog';
import { AudienceViewDialog } from './AudienceViewDialog';
import type { useRoom } from './useRoom';

interface Props {
  state: ReturnType<typeof useRoom>;
}

export function RoomControl({ state }: Props) {
  const { room, currentIndex, next, prev, jumpTo, close, clearRoomAnchor, animation, animationControl } = state;
  // Clip names of the model currently shown; the transport only renders
  // when this is non-empty (model has animation capability).
  const [clips, setClips] = useState<string[]>([]);
  const [selectedClip, setSelectedClip] = useState<string>('');
  useEffect(() => {
    setSelectedClip((prev) => (clips.includes(prev) ? prev : clips[0] ?? ''));
  }, [clips]);
  // Follow whatever clip is actually live (e.g. set by another operator).
  useEffect(() => {
    if (animation.clip && clips.includes(animation.clip)) setSelectedClip(animation.clip);
  }, [animation.clip, clips]);
  const assets = room?.sequence?.assets ?? [];
  const current = assets[currentIndex];

  const [access, setAccess] = useState<RoomAccess | null>(null);
  const refreshAccess = useCallback(async () => {
    if (!room) return;
    try {
      setAccess(await api.getRoomAccess(room.id));
    } catch { /* silent — dialog will surface any real error */ }
  }, [room]);
  useEffect(() => { void refreshAccess(); }, [refreshAccess]);

  // Fetched once for the sequence list's per-model animation-clip badge
  // (see modelBasename/animCountFor below) — /models already computes
  // this cheaply server-side (GLB header only, not the full file), this
  // just reuses that existing listing rather than adding a new endpoint.
  const [models, setModels] = useState<ModelFile[]>([]);
  useEffect(() => {
    api.listModels().then(setModels).catch(() => setModels([]));
  }, []);
  const modelBasename = (modelPath: string) => modelPath.split('/').pop()?.split('?')[0] ?? modelPath;
  const animCountFor = (modelPath: string) =>
    models.find((m) => m.name === modelBasename(modelPath))?.animationCount;

  // Overrides room.joinCode after an explicit regenerate, until the next
  // full room refetch — the socket-driven room object has no setter of
  // its own for this. Reset whenever we land on a different room so a
  // stale override can't leak across rooms.
  const [joinCodeOverride, setJoinCodeOverride] = useState<string | null>(null);
  useEffect(() => { setJoinCodeOverride(null); }, [room?.id]);
  const [regenerating, setRegenerating] = useState(false);
  const joinCode = joinCodeOverride ?? room?.joinCode ?? null;
  const regenerateJoinCode = async () => {
    if (!room) return;
    setRegenerating(true);
    try {
      const { join_code } = await api.regenerateJoinCode(room.id);
      setJoinCodeOverride(join_code);
      toast.success('New join code generated', {
        description: `Old code no longer works. New code: ${join_code}`,
      });
    } catch (err) {
      toast.error('Failed to regenerate join code', {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setRegenerating(false);
    }
  };

  useHotkeys('right', () => next(), [next]);
  useHotkeys('left', () => prev(), [prev]);
  useHotkeys('1,2,3,4,5,6,7,8,9', (_, handler) => {
    const n = Number(handler.keys?.[0]);
    if (!Number.isFinite(n)) return;
    if (n - 1 < assets.length) jumpTo(n - 1);
  });
  useHotkeys('esc', () => close());

  if (!room) return null;

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
      {/* --- Main control column ------------------------------------- */}
      <div className="flex flex-col gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={close}
          className="w-fit -ml-2 text-muted-foreground hover:text-foreground"
          title="Back to room list (leaves this room)"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to rooms
        </Button>
        <Card>
          <CardHeader className="flex flex-col gap-3">
            <div className="min-w-0">
              <CardTitle className="truncate text-xl">{room.name}</CardTitle>
              <CardDescription className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
                <span className="flex items-center gap-1.5">
                  <Users className="h-4 w-4" />
                  {room.userCount} in room
                </span>
                {room.deviceBreakdown && (room.deviceBreakdown.mobile > 0 || room.deviceBreakdown.xr_headset > 0) && (
                  <span className="flex items-center gap-1">
                    {room.deviceBreakdown.mobile > 0 && (
                      <DeviceBadge type="mobile" count={room.deviceBreakdown.mobile} />
                    )}
                    {room.deviceBreakdown.xr_headset > 0 && (
                      <DeviceBadge type="xr_headset" count={room.deviceBreakdown.xr_headset} />
                    )}
                  </span>
                )}
                <span>
                  Model {currentIndex + 1} of {assets.length}
                </span>
                {joinCode && (
                  <span
                    className="flex items-center gap-1.5 rounded-md bg-primary/10 px-2 py-0.5 font-mono text-base font-semibold tracking-widest text-primary"
                    title="Read this out to headset users — no URL needed, they type it in on their end. Expires after 24h, or immediately once regenerated."
                  >
                    <KeyRound className="h-4 w-4" />
                    {joinCode}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                  disabled={regenerating}
                  onClick={regenerateJoinCode}
                  title="Invalidate the current join code and generate a new one — do this before a new audience if the old code might still be floating around"
                >
                  <RefreshCw className={`h-3 w-3 ${regenerating ? 'animate-spin' : ''}`} />
                  New code
                </Button>
                <VisibilityChip access={access} />
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const url = `${window.location.pathname}?audience=${room.id}`;
                  window.open(url, `casper-audience-${room.id}`, 'width=1600,height=900');
                }}
                title="Open a projector-friendly view showing the QR + marker"
              >
                <MonitorPlay className="h-4 w-4" /> Audience view
              </Button>
              <AudienceViewDialog roomId={room.id} />
              <RoomSettingsDialog
                roomId={room.id}
                roomName={room.name}
                onAccessChanged={setAccess}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={close}
                title="Leave room (server keeps running)"
              >
                <LogOut className="h-4 w-4" /> Leave
              </Button>
              <EndSessionDialog
                roomId={room.id}
                roomName={room.name}
                createdByEmail={access?.createdByEmail}
                onClosed={close}
              />
            </div>
          </CardHeader>

          <CardContent className="grid gap-4">
            {current ? (
              <ModelPreview
                modelPath={current.model}
                externalURL={current.externalURL}
                scale={parseVec3Scalar(current.scaling)}
                onClips={setClips}
              />
            ) : (
              <div className="grid h-72 place-items-center rounded-md border border-dashed text-sm text-muted-foreground">
                Sequence has no assets.
              </div>
            )}

            {clips.length > 0 && (
              <div className="grid gap-3 rounded-md border bg-muted/30 p-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Film className="h-4 w-4" /> Animation
                  <span className="text-xs font-normal text-muted-foreground">
                    {animation.state === 'stopped' ? 'Stopped' : animation.state === 'playing' ? 'Playing' : 'Paused'} · synced to all clients
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {clips.length > 1 && (
                    <Select value={selectedClip} onValueChange={setSelectedClip}>
                      <SelectTrigger className="min-w-[180px] max-w-[260px]" aria-label="Animation clip">
                        <SelectValue placeholder="Select animation…" />
                      </SelectTrigger>
                      <SelectContent>
                        {clips.map((c, i) => (
                          <SelectItem key={`${c}-${i}`} value={c}>{c || `Clip ${i + 1}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {animation.state === 'playing' ? (
                    <Button variant="secondary" onClick={() => animationControl({ action: 'pause' })}>
                      <Pause className="h-4 w-4" /> Pause
                    </Button>
                  ) : animation.state === 'paused' && animation.clip === selectedClip ? (
                    <Button onClick={() => animationControl({ action: 'resume' })}>
                      <Play className="h-4 w-4" /> Resume
                    </Button>
                  ) : (
                    <Button onClick={() => animationControl({ action: 'play', clip: selectedClip, loop: animation.loop })}>
                      <Play className="h-4 w-4" /> Start
                    </Button>
                  )}
                  {animation.state === 'playing' && animation.clip !== selectedClip && (
                    <Button onClick={() => animationControl({ action: 'play', clip: selectedClip, loop: animation.loop })}>
                      <Play className="h-4 w-4" /> Switch to this
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => animationControl({ action: 'stop' })}
                    disabled={animation.state === 'stopped'}
                  >
                    <Square className="h-4 w-4" /> Stop
                  </Button>
                  <Button
                    variant={animation.loop ? 'default' : 'secondary'}
                    aria-pressed={animation.loop}
                    onClick={() => animationControl({ action: 'setLoop', loop: !animation.loop })}
                    title="Loop the animation"
                  >
                    <Repeat className="h-4 w-4" /> Loop {animation.loop ? 'on' : 'off'}
                  </Button>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" onClick={prev} disabled={assets.length < 2}>
                <ChevronLeft className="h-4 w-4" /> Previous
              </Button>
              <Button onClick={next} disabled={assets.length < 2}>
                Next <ChevronRight className="h-4 w-4" />
              </Button>

              <div className="ml-auto flex min-w-[220px] items-center gap-2">
                <span className="text-xs text-muted-foreground">Jump to</span>
                <Select
                  value={current?.model ?? ''}
                  onValueChange={(model) => {
                    const idx = assets.findIndex((a) => a.model === model);
                    if (idx >= 0) jumpTo(idx);
                  }}
                >
                  <SelectTrigger className="min-w-[180px]">
                    <SelectValue placeholder="Select model…" />
                  </SelectTrigger>
                  <SelectContent>
                    {assets.map((a, i) => (
                      <SelectItem key={a.model} value={a.model}>
                        <span className="mr-2 tabular-nums text-muted-foreground">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        {prettyModelName(a.model)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Sequence</CardTitle>
            <CardDescription>
              Click any row to jump. Keyboard: ← / → to step, 1–9 to jump, Esc to close.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ol className="divide-y divide-border">
              {assets.map((a, i) => {
                const active = i === currentIndex;
                return (
                  <li key={a.model}>
                    <button
                      type="button"
                      onClick={() => jumpTo(i)}
                      className={`flex w-full items-center gap-3 px-6 py-2.5 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground ${
                        active ? 'bg-muted font-medium text-primary' : ''
                      }`}
                    >
                      <span className="w-6 shrink-0 text-right tabular-nums text-xs text-muted-foreground">
                        {i + 1}
                      </span>
                      <span className="truncate">{prettyModelName(a.model)}</span>
                      {!!animCountFor(a.model) && (
                        <span
                          className="flex shrink-0 items-center gap-1 text-[10px] text-muted-foreground"
                          title={`${animCountFor(a.model)} animation clip(s) embedded in this model`}
                        >
                          <Film className="h-3 w-3" />
                          {animCountFor(a.model)}
                        </span>
                      )}
                      {active && (
                        <span className="ml-auto text-[10px] uppercase tracking-widest text-primary">
                          Playing
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>
      </div>

      {/* --- Right rail: per-device join info + external URL -------- */}
      <div className="flex flex-col gap-4">
        <MobileArCard
          roomId={room.id}
          onCopy={(url) =>
            toast.success('Client URL copied', {
              description: url,
            })
          }
        />
        <HeadsetCard
          roomId={room.id}
          joinCode={joinCode}
          anchorPose={room.anchorPose}
          onClearAnchor={() => {
            clearRoomAnchor();
            toast.message('Room anchor cleared', {
              description: 'Headsets fall back to their own default placement on next recenter.',
            });
          }}
          onCopy={(url) =>
            toast.success('Headset link copied', {
              description: url,
            })
          }
        />
        {current?.externalURL ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                External link
                <DeviceBadge type="mobile" variant="label" className="text-[10px] font-normal uppercase tracking-wide" />
              </CardTitle>
              <CardDescription>
                Opens in the mobile AR client&apos;s overlay button. Not available on the XR headset
                client — WebXR&apos;s dom-overlay isn&apos;t supported on real headset hardware.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <a
                href={ensureAbsolute(current.externalURL)}
                target="_blank"
                rel="noreferrer"
                className="block truncate text-sm text-primary hover:underline"
              >
                {current.externalURL}
              </a>
              <div className="mt-3 flex">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    void navigator.clipboard.writeText(current.externalURL!);
                    toast.success('URL copied');
                  }}
                >
                  Copy
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Keyboard</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
              <dt>
                <Kbd>←</Kbd> / <Kbd>→</Kbd>
              </dt>
              <dd>Previous / next</dd>
              <dt>
                <Kbd>1</Kbd>–<Kbd>9</Kbd>
              </dt>
              <dd>Jump to model N</dd>
              <dt>
                <Kbd>Esc</Kbd>
              </dt>
              <dd>Close room</dd>
            </dl>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-foreground">
      {children}
    </kbd>
  );
}

function VisibilityChip({ access }: { access: RoomAccess | null }) {
  if (!access) return null;
  if (access.closed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-sm bg-destructive/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-destructive">
        <Ban className="h-3 w-3" /> Ended
      </span>
    );
  }
  const meta = {
    public:        { icon: Users2,      label: 'Public',      className: 'bg-muted text-muted-foreground' },
    authenticated: { icon: Lock,        label: 'Signed-in',   className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
    admin:         { icon: ShieldAlert, label: 'Admins only', className: 'bg-primary/10 text-primary' },
  }[access.visibility];
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${meta.className}`}
      title={`Visibility: ${access.visibility}`}
    >
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

function ensureAbsolute(url: string) {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

/**
 * Parse an A-Frame vec3 string (e.g. "1 1 1") into a single scalar. The
 * AR client applies auto-fit so all three components should be equal;
 * we take the first component and fall back to 1 if unparseable.
 */
function parseVec3Scalar(v?: string): number | undefined {
  if (!v) return undefined;
  const first = parseFloat(String(v).trim().split(/\s+/)[0]);
  return Number.isFinite(first) && first > 0 ? first : undefined;
}
